import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Calendar, Eye, ChevronRight, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const currentCategory = searchParams.get('category') || '';

  const categories = [
    'All',
    'Emerging Topics',
    'Finance',
    'Fintech',
    'Regulatory',
    'Education',
    'Design'
  ];

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = currentCategory && currentCategory !== 'All' 
          ? `?category=${encodeURIComponent(currentCategory)}` 
          : '';
        const response = await axios.get(`${API}/posts${params}`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [currentCategory]);

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCategoryChange = (category) => {
    if (category === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = (platform, post) => {
    const url = `${window.location.origin}/blog/${post.slug}`;
    const text = post.title;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen" data-testid="blog-page">
      {/* Hero */}
      <section className="bg-white py-16 md:py-24 border-b border-primary/10">
        <div className="container-padding">
          <div className="max-w-3xl">
            <p className="label-uppercase mb-4">Insights & Analysis</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-primary mb-6">
              Finance Insights
            </h1>
            <p className="text-xl text-secondary font-serif leading-relaxed">
              Deep analysis on emerging finance topics, regulatory developments, fintech innovations, 
              and the global economic forces shaping our financial future.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-background border-b border-primary/10 sticky top-20 z-40">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 py-2 text-sm font-sans font-medium transition-colors ${
                    (category === 'All' && !currentCategory) || category === currentCategory
                      ? 'bg-primary text-white'
                      : 'bg-white text-primary border border-primary/20 hover:bg-primary/5'
                  }`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
            
            {/* Search */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
              <Input
                type="search"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none border-primary/20"
                data-testid="blog-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="section-padding">
        <div className="container-padding">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[16/9] bg-primary/10 mb-4"></div>
                  <div className="h-4 bg-primary/10 w-1/4 mb-3"></div>
                  <div className="h-6 bg-primary/10 w-3/4 mb-2"></div>
                  <div className="h-4 bg-primary/10 w-full mb-2"></div>
                  <div className="h-4 bg-primary/10 w-2/3"></div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-secondary font-serif text-lg">No articles found.</p>
              <Button 
                onClick={() => { setSearchQuery(''); handleCategoryChange('All'); }}
                variant="link"
                className="text-accent mt-4"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <article 
                  key={post.post_id} 
                  className="group"
                  data-testid={`blog-post-${index}`}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-[16/9] overflow-hidden border border-primary/10 mb-4">
                      <img 
                        src={post.featured_image || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <span className="label-uppercase">{post.category}</span>
                    <span className="text-secondary text-sm flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.created_at)}
                    </span>
                  </div>
                  
                  <Link to={`/blog/${post.slug}`}>
                    <h2 className="font-sans text-xl font-medium text-primary mb-2 group-hover:text-accent transition-colors">
                      {post.title}
                    </h2>
                  </Link>
                  
                  <p className="text-secondary font-serif text-sm leading-relaxed line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="text-accent text-sm font-sans font-medium flex items-center gap-1 hover:gap-2 transition-all"
                    >
                      Read More <ChevronRight className="w-4 h-4" />
                    </Link>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-secondary text-xs flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.views}
                      </span>
                      <button 
                        onClick={() => sharePost('linkedin', post)}
                        className="w-7 h-7 flex items-center justify-center text-secondary hover:text-accent transition-colors"
                        aria-label="Share on LinkedIn"
                      >
                        <Linkedin className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => sharePost('twitter', post)}
                        className="w-7 h-7 flex items-center justify-center text-secondary hover:text-accent transition-colors"
                        aria-label="Share on Twitter"
                      >
                        <Twitter className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
